import debounce from 'debounce';
import type { SagaIterator } from 'redux-saga';
import { eventChannel } from 'redux-saga';
import { call, getContext, put, take } from 'redux-saga/effects';
import type { Socket } from 'socket.io-client';

import tasksApi from '@app/api/tasks';

type TaskChannelEvent = {
    invalidatesAll: boolean;
    taskId?: string;
};

function createSocketChannel(socketConnection: Socket) {
    // `eventChannel` takes a subscriber function
    // the subscriber function takes an `emit` argument to put messages onto the channel
    return eventChannel<TaskChannelEvent>((emit) => {
        const emitChannelMessage = debounce((invalidatesAll: boolean, taskId?: string) => {
            emit({
                invalidatesAll,
                taskId,
            });
        }, 1000);

        const documentEventHandler = (ev: CustomEvent) => emitChannelMessage(false, ev.detail.taskId);

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const taskInvalidatingMsgHandler = ({ task_id }: { task_id: number }) =>
            emitChannelMessage(false, String(task_id));

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const listInvalidatingMsgHandler = ({ task_id }: { task_id: number }) =>
            emitChannelMessage(true, String(task_id));

        // When something changes in current tab
        document.addEventListener('task_item_complete', documentEventHandler);
        document.addEventListener('task_lang_complete', documentEventHandler);
        document.addEventListener('task_closed', documentEventHandler);
        document.addEventListener('task_language_assignees_changed', documentEventHandler);
        document.addEventListener('task_deleted', documentEventHandler);

        // When something changes in other browsers/tabs
        socketConnection.on('task item complete', taskInvalidatingMsgHandler);
        socketConnection.on('task lang complete', taskInvalidatingMsgHandler);
        socketConnection.on('task created', listInvalidatingMsgHandler); // invalidates whole list to load potentially added tasks
        socketConnection.on('task language assignees changed', listInvalidatingMsgHandler); // invalidates whole list to load potentially added tasks
        socketConnection.on('task closed', taskInvalidatingMsgHandler);
        socketConnection.on('task deleted', taskInvalidatingMsgHandler);

        // the subscriber must return an unsubscribe function
        // this will be invoked when the saga calls `channel.close` method
        /* istanbul ignore next */
        return () => {
            document.removeEventListener('task_item_complete', documentEventHandler);
            document.removeEventListener('task_lang_complete', documentEventHandler);
            document.removeEventListener('task_closed', documentEventHandler);
            document.removeEventListener('task_language_assignees_changed', documentEventHandler);
            document.removeEventListener('task_deleted', documentEventHandler);

            socketConnection.off('task item complete', taskInvalidatingMsgHandler);
            socketConnection.off('task lang complete', taskInvalidatingMsgHandler);
            socketConnection.off('task created', listInvalidatingMsgHandler);
            socketConnection.off('task language assignees changed', listInvalidatingMsgHandler);
            socketConnection.off('task closed', taskInvalidatingMsgHandler);
            socketConnection.off('task deleted', taskInvalidatingMsgHandler);
        };
    });
}

export default function* initProjectTasksSagas(): SagaIterator {
    const socketConnection = yield getContext('socketConnection');
    const socketChannel = yield call(createSocketChannel, socketConnection);

    while (true) {
        // Listen for events
        const event: TaskChannelEvent = yield take(socketChannel);

        // And invalidate tags
        // Both cause a full reload of task widget data
        // But selective invalidation only triggers if this task has been previously loaded
        if (event.invalidatesAll) {
            yield put(tasksApi.util.invalidateTags([{ type: 'TASKS_LIST', id: 'LIST' }]));
        } else if (event.taskId) {
            yield put(tasksApi.util.invalidateTags([{ type: 'TASKS_LIST', id: event.taskId }]));
        }

        yield put(tasksApi.util.invalidateTags([{ type: 'TASKS_BADGE', id: 'LIST' }]));
    }
}
